"use client"

import * as React from "react"
import { useTranslation } from "react-i18next"
import { Check, ChevronsUpDown, Clock } from "lucide-react"
import { cn } from "../../lib/utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../Command/Command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Popover/Popover"

type TimezoneOption = {
  value: string
  label: string
  region: string
  offset: string
}

type Props = {
  value?: string
  onChange: (value: string) => void
  label?: string
  isDisabled?: boolean
  helperText?: string
  timezones?: string[]
}

const getTimezoneOffset = (timezone: string): string => {
  try {
    const now = new Date()
    // Create a formatter for the target timezone
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    })
    
    // Try to extract offset from formatted string
    const formatted = formatter.formatToParts(now)
    const offsetPart = formatted.find(part => 
      part.type === 'timeZoneName' && part.value.includes('GMT')
    )
    
    if (offsetPart) {
      // Extract GMT offset like "GMT+1" or "GMT-5"
      const match = offsetPart.value.match(/GMT([+-])(\d+)/)
      if (match) {
        const sign = match[1]
        const hours = parseInt(match[2])
        return `${sign}${String(hours).padStart(2, '0')}:00`
      }
    }
    
    // Fallback: calculate offset manually
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
    const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60)
    const sign = offset >= 0 ? '+' : '-'
    const hours = Math.floor(Math.abs(offset))
    const minutes = Math.floor((Math.abs(offset) - hours) * 60)
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  } catch {
    return ''
  }
}

const getRegion = (timezone: string): string => {
  const parts = timezone.split('/')
  if (parts.length > 1) {
    return parts[0]
  }
  if (['GMT', 'UTC', 'CET', 'EET', 'WET', 'EST', 'PST', 'CST', 'MST'].includes(timezone)) {
    return 'Other'
  }
  return 'Other'
}

const formatTimezoneLabel = (timezone: string): string => {
  const parts = timezone.split('/')
  if (parts.length > 1) {
    // Convert "Europe/Warsaw" to "Warsaw, Europe"
    const city = parts[parts.length - 1].replace(/_/g, ' ')
    const region = parts[0]
    return `${city}, ${region}`
  }
  return timezone
}

// Process timezones into grouped options
const processTimezones = (timezones: string[]): TimezoneOption[] => {
  return timezones.map(tz => ({
    value: tz,
    label: formatTimezoneLabel(tz),
    region: getRegion(tz),
    offset: getTimezoneOffset(tz)
  })).sort((a, b) => {
    // Sort by region first, then by label
    if (a.region !== b.region) {
      return a.region.localeCompare(b.region)
    }
    return a.label.localeCompare(b.label)
  })
}

const groupByRegion = (options: TimezoneOption[]): Map<string, TimezoneOption[]> => {
  const grouped = new Map<string, TimezoneOption[]>()
  options.forEach(option => {
    const region = option.region
    if (!grouped.has(region)) {
      grouped.set(region, [])
    }
    grouped.get(region)!.push(option)
  })
  return grouped
}

const REGION_ORDER = [
  'Europe',
  'America',
  'Asia',
  'Africa',
  'Australia',
  'Pacific',
  'Atlantic',
  'Indian',
  'Antarctica',
  'Arctic',
  'Other'
]

export function TimezoneSelector({
  value,
  onChange,
  label,
  isDisabled = false,
  helperText,
  timezones = []
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const comboboxId = React.useId()

  const processedTimezones = React.useMemo(() => {
    return processTimezones(timezones.length > 0 ? timezones : [])
  }, [timezones])

  const groupedTimezones = React.useMemo(() => {
    return groupByRegion(processedTimezones)
  }, [processedTimezones])

  const selectedOption = React.useMemo(() => {
    return processedTimezones.find(opt => opt.value === value)
  }, [processedTimezones, value])

  const sortedRegions = React.useMemo(() => {
    const regions = Array.from(groupedTimezones.keys())
    return regions.sort((a, b) => {
      const indexA = REGION_ORDER.indexOf(a)
      const indexB = REGION_ORDER.indexOf(b)
      if (indexA === -1 && indexB === -1) return a.localeCompare(b)
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })
  }, [groupedTimezones])

  return (
    <div className="flex-1 max-w-full">
      {label && (
        <p className="text-sm font-extrabold">{t(label)}:</p>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-controls={comboboxId}
            className={cn(
              "px-1 pt-3 pb-3 w-full border-stone-200 border rounded focus:outline-none focus:ring-0 text-left flex items-center justify-between",
              isDisabled ? 'bg-stone-100 cursor-not-allowed' : 'cursor-pointer',
              !selectedOption && "text-gray-500"
            )}
            disabled={isDisabled}
            data-dd-action-name="Dropdown Select"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectedOption ? (
                <>
                  <Clock className="h-4 w-4 shrink-0 opacity-50" />
                  <span className="truncate">{selectedOption.label}</span>
                  {selectedOption.offset && (
                    <span className="text-xs text-gray-500 ml-auto shrink-0">
                      {selectedOption.offset}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">
                  {t("global.null") || "Select timezone..."}
                </span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent id={comboboxId} className="w-[400px] p-0" align="start">
          <Command
            filter={(value, search) => {
              if (!search) return 1
              
              const searchLower = search.toLowerCase().trim()
              const valueLower = value.toLowerCase()
              
              // Exact match gets highest priority
              if (valueLower === searchLower) return 1
              
              // Split into words for more precise matching
              const words = valueLower.split(/[\s/,]+/).filter(w => w.length > 0)
              
              // Check if search matches the beginning of any word
              const startsWithMatch = words.some(word => word.startsWith(searchLower))
              if (startsWithMatch) return 1
              
              // Check if search is contained in any word (substring match)
              const containsMatch = words.some(word => word.includes(searchLower))
              if (containsMatch) return 1
              
              if (searchLower.length <= 2) {
                const exactWordMatch = words.some(word => word === searchLower)
                if (exactWordMatch) return 1
                return 0
              }
              
              // For longer searches, allow fuzzy matching but require characters to be closer together
              // This prevents "cet" from matching "Bucharest" (c, e, t are too scattered)
              let searchIndex = 0
              let lastMatchIndex = -1
              let maxGap = 0
              
              for (let i = 0; i < valueLower.length && searchIndex < searchLower.length; i++) {
                if (valueLower[i] === searchLower[searchIndex]) {
                  if (lastMatchIndex >= 0) {
                    const gap = i - lastMatchIndex
                    maxGap = Math.max(maxGap, gap)
                  }
                  lastMatchIndex = i
                  searchIndex++
                }
              }
              
              // Only allow fuzzy match if:
              // 1. All characters were found
              // 2. Characters are reasonably close together (max gap <= search length * 2)
              if (searchIndex === searchLower.length && maxGap <= searchLower.length * 2) {
                return 1
              }
              
              return 0
            }}
          >
            <CommandInput 
              placeholder={t("settings.general.basic_info.timezone") || "Search timezone..."} 
              className="h-9"
            />
            <CommandList className="max-h-[300px]">
              <CommandEmpty>
                {t("global.empty_results") || "No timezone found."}
              </CommandEmpty>
              {sortedRegions.map((region) => {
                const regionTimezones = groupedTimezones.get(region) || []
                if (regionTimezones.length === 0) return null

                return (
                  <CommandGroup key={region} heading={region}>
                    {regionTimezones.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={`${option.value} ${option.label} ${option.region}`}
                        onSelect={() => {
                          onChange(option.value)
                          setOpen(false)
                        }}
                        data-dd-action-name="Dropdown Select Option"
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Check
                            className={cn(
                              "h-4 w-4 shrink-0",
                              value === option.value ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="truncate">{option.label}</span>
                            {option.offset && (
                              <span className="text-xs text-muted-foreground">
                                {option.offset}
                              </span>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {helperText && (
        <p className="text-xs text-gray-500 mt-1">{helperText}</p>
      )}
    </div>
  )
}

